import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getAssessmentContext, summarizeAssessmentReadiness, type AssessmentWorkflow } from '../../lib/services/assessments'
import { getFacilities } from '../../lib/services/facilities'
import { createAssessmentVisit, getAssignments } from '../../lib/services/visits'

export function AssessmentWorkflowPage({ workflow }: { workflow: AssessmentWorkflow }) {
  const { viewerContext } = useAuth()
  const context = getAssessmentContext(workflow)
  const [facilityId, setFacilityId] = useState('')
  const [surveyDate, setSurveyDate] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)

  const facilitiesQuery = useQuery({
    queryKey: ['assessment-facilities'],
    queryFn: getFacilities,
  })

  const visitsQuery = useQuery({
    queryKey: ['assessment-visits', workflow],
    queryFn: getAssignments,
  })

  const workflowVisits = useMemo(
    () => (visitsQuery.data || []).filter((visit) => visit.visit_type === workflow),
    [visitsQuery.data, workflow],
  )
  const readiness = summarizeAssessmentReadiness(workflowVisits)

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">{context.title}</h1>
        <p className="mt-1 text-sm text-slate-600">{context.framing}</p>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="v11-kpi-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Assessments</p>
          <p className="mt-1 text-3xl font-extrabold text-[var(--brand-deep)]">{readiness.total}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">In Progress</p>
          <p className="mt-1 text-3xl font-extrabold text-[var(--brand-deep)]">{readiness.in_progress}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-teal">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Readiness</p>
          <p className="mt-1 text-2xl font-extrabold text-[var(--brand-deep)]">{readiness.readiness}</p>
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">{context.creationLabel}</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
          >
            <option value="">Select Facility</option>
            {(facilitiesQuery.data || []).map((facility) => (
              <option key={facility.facility_id} value={facility.facility_id}>
                {facility.facility_name}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={surveyDate}
            onChange={(e) => setSurveyDate(e.target.value)}
          />
          <button
            className="btn-primary text-sm"
            disabled={isActing || !facilityId || !viewerContext?.user_id}
            onClick={async () => {
              if (!viewerContext?.user_id) return
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                await createAssessmentVisit({
                  facility_id: facilityId,
                  visit_type: workflow,
                  assigned_surveyor_id: viewerContext.user_id,
                  survey_date: surveyDate || undefined,
                })
                setFacilityId('')
                setSurveyDate('')
                setActionSuccess('Assessment visit created.')
                await visitsQuery.refetch()
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            Create Assessment
          </button>
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">{context.executionLabel}</h2>
        {visitsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading assessment visits...</p>}
        {visitsQuery.error && <p className="mt-2 text-sm text-red-600">{(visitsQuery.error as Error).message}</p>}
        {!visitsQuery.isLoading && !visitsQuery.error && workflowVisits.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No assessment visits found for this workflow.</p>
        )}
        <div className="mt-3 grid gap-3">
          {workflowVisits.map((visit) => (
            <div key={visit.id} className="v11-dashboard-card p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900">{visit.facility_name}</p>
                <span className={visit.status === 'released' ? 'badge-released' : 'badge-in-progress'}>{visit.status}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{visit.visit_date}</p>
              <Link to={context.executionUrl(visit.id)} className="mt-2 inline-block text-xs font-semibold text-[var(--brand-primary)] underline">
                Open Execution
              </Link>
            </div>
          ))}
        </div>
      </section>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {actionSuccess && <p className="text-sm text-emerald-700">{actionSuccess}</p>}
    </div>
  )
}
